/**
 * LayoutFormModal — create a floor plan layout, or rename / re-image / re-default one.
 *
 * The file is read in the browser: its natural size is measured (that becomes the canvas
 * content box and the frame every pin's 0..1 position is relative to) and its bytes are
 * base64'd. Base64 is the wire format on BOTH nodes — the cloud must encode anyway to cross
 * the tunnel, so using one representation keeps a single code path on the backend.
 *
 * Laid out plan-first, in two columns: the drawing is the substance of a layout and a name
 * is a label on it, so the plan gets the space and the fields sit beside it rather than
 * pushing it into a tall scroll. Editing shows the CURRENT plan straight away — otherwise
 * "replace image" is a decision made blind, and a wrong file isn't discovered until twenty
 * pins are already on it.
 */
import { useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ExclamationCircleIcon,
  MapPinIcon,
  PhotoIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import type { MapLayoutInput, MapLayoutSummary } from '../../api/contracts';

/** Mirrors the node's allowlist (cameraMapService.ALLOWED_MIME). */
const ACCEPT = 'image/svg+xml,image/png,image/jpeg,image/webp';
const ALLOWED = new Set(['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp']);
/** Mirrors the node's cap. Checked here too so an oversized plan fails instantly. */
const MAX_BYTES = 5 * 1024 * 1024;

const fmtBytes = (n: number) =>
  n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`;

interface PickedImage {
  mime: string;
  /** base64, no `data:` prefix. */
  data: string;
  width: number;
  height: number;
  /** For the preview. */
  dataUrl: string;
  fileName: string;
  size: number;
}

/**
 * An SVG's intrinsic size. `<img>.naturalWidth` is unreliable for SVG — a file with only a
 * viewBox reports the browser's 300×150 default in Chrome and 0 in Firefox, either of which
 * would place every pin against the wrong frame. So read the markup instead.
 */
function svgDimensions(text: string): { width: number; height: number } {
  try {
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    const svg = doc.documentElement;
    const num = (v: string | null) => {
      const n = parseFloat((v || '').replace(/[a-z%]+$/i, ''));
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    const w = num(svg.getAttribute('width'));
    const h = num(svg.getAttribute('height'));
    if (w && h) return { width: Math.round(w), height: Math.round(h) };
    const vb = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) return { width: Math.round(vb[2]), height: Math.round(vb[3]) };
  } catch {
    /* fall through to the default box below */
  }
  // A plan with neither is still usable — the pins just live in a 1000×1000 frame.
  return { width: 1000, height: 1000 };
}

function readFile(file: File): Promise<PickedImage> {
  return new Promise((resolve, reject) => {
    if (!ALLOWED.has(file.type)) {
      reject(new Error(`${file.name || 'That file'} isn't a supported image. Use SVG, PNG, JPEG or WebP.`));
      return;
    }
    if (file.size > MAX_BYTES) {
      reject(new Error(`That file is ${fmtBytes(file.size)}. Floor plans must be 5 MB or smaller.`));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = async () => {
      const dataUrl = String(reader.result || '');
      const data = dataUrl.replace(/^data:[^;]+;base64,/, '');
      const base = { mime: file.type, data, dataUrl, fileName: file.name, size: file.size };
      if (file.type === 'image/svg+xml') {
        resolve({ ...base, ...svgDimensions(await file.text()) });
        return;
      }
      const img = new Image();
      img.onload = () => resolve({ ...base, width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('That image could not be decoded.'));
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

/** Small pill used for the plan's dimensions / size / format. */
function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{children}</span>
  );
}

export interface LayoutFormModalProps {
  open: boolean;
  /** Editing an existing layout, or undefined to create a new one. */
  layout?: MapLayoutSummary | null;
  /** Caller holds `map.edit`. */
  canEdit?: boolean;
  /** Resolves a layout's current plan for the preview (local URL / cloud data URL). */
  resolveImage?: (layout: MapLayoutSummary) => Promise<string>;
  onClose: () => void;
  /** `thenMark` creates the layout and drops the operator straight into placing pins. */
  onCreate: (
    input: MapLayoutInput & { is_default?: boolean },
    opts?: { thenMark?: boolean },
  ) => Promise<void>;
  onUpdate: (id: string, patch: Partial<MapLayoutInput> & { is_default?: boolean }) => Promise<void>;
  /** Jump straight from here into placing pins on an EXISTING layout. */
  onMarkCameras?: (layout: MapLayoutSummary) => void;
}

export default function LayoutFormModal({
  open,
  layout,
  canEdit = true,
  resolveImage,
  onClose,
  onCreate,
  onUpdate,
  onMarkCameras,
}: LayoutFormModalProps) {
  const editing = !!layout;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [picked, setPicked] = useState<PickedImage | null>(null);
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [touchedName, setTouchedName] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setName(layout?.name || '');
    setDescription((layout?.description as string) || '');
    setMakeDefault(!!layout?.is_default);
    setPicked(null);
    setCurrentSrc(null);
    setDragOver(false);
    setTouchedName(false);
    setError('');
  }, [open, layout]);

  // The existing plan, so "replace" is a comparison rather than a guess. Failure is silent:
  // the panel falls back to the upload prompt, which is still a working dialog.
  useEffect(() => {
    if (!open || !layout || !resolveImage) return;
    let mounted = true;
    resolveImage(layout)
      .then((src) => mounted && setCurrentSrc(src))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [open, layout, resolveImage]);

  async function pick(file?: File | null) {
    if (!file) return;
    setError('');
    try {
      setPicked(await readFile(file));
    } catch (e) {
      setPicked(null);
      setError((e as Error).message);
    }
  }

  const nameError = touchedName && !name.trim() ? 'A layout needs a name.' : '';
  const missingImage = !editing && !picked;
  const canSubmit = !!name.trim() && !missingImage && !saving;

  async function submit(thenMark = false) {
    setTouchedName(true);
    if (!name.trim()) return;
    if (missingImage) {
      setError('Upload a floor plan image.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const image = picked
        ? {
            imageMime: picked.mime,
            imageData: picked.data,
            imageWidth: picked.width,
            imageHeight: picked.height,
          }
        : {};
      if (editing) {
        await onUpdate(layout!.id, {
          name: name.trim(),
          description: description.trim(),
          ...image,
          // Only ever SET the default — a layout is un-defaulted by making another one
          // default, never by leaving the estate with none.
          ...(makeDefault && !layout!.is_default ? { is_default: true } : {}),
        });
      } else {
        await onCreate(
          {
            name: name.trim(),
            description: description.trim(),
            ...(image as Required<typeof image>),
            ...(makeDefault ? { is_default: true } : {}),
          } as MapLayoutInput & { is_default?: boolean },
          { thenMark },
        );
      }
      onClose();
    } catch (e) {
      setError((e as Error).message || 'Could not save the layout.');
    } finally {
      setSaving(false);
    }
  }

  const field =
    'w-full rounded-lg border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 transition-colors focus:outline-none focus:ring-1';
  const fieldOk = `${field} border-border focus:border-brand focus:ring-brand`;
  const fieldBad = `${field} border-destructive focus:border-destructive focus:ring-destructive`;

  /** Drop handlers shared by the empty prompt and the filled preview. */
  const dropProps = {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(true);
    },
    onDragLeave: () => setDragOver(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      pick(e.dataTransfer.files?.[0]);
    },
  };

  const previewSrc = picked?.dataUrl || currentSrc;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit layout' : 'New layout'}
      subtitle={editing ? layout?.name : 'Upload a floor plan, then mark cameras on it'}
      size="xl"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Left: the natural next step after saving a plan is putting cameras on it.
              This dialog is the ONLY route into marking — the page toolbar deliberately
              doesn't offer it, since placing pins belongs to a specific layout. */}
          <div>
            {editing && canEdit && onMarkCameras && (
              <button
                type="button"
                onClick={() => onMarkCameras(layout!)}
                className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <MapPinIcon className="h-4 w-4" /> Mark cameras
              </button>
            )}
            {/* A layout has no id until it's saved, so the create-side equivalent has to
                save first — one press instead of create, reopen, mark. */}
            {!editing && canEdit && (
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={!canSubmit}
                className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              >
                <MapPinIcon className="h-4 w-4" /> Create &amp; mark cameras
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Say WHY Save is unavailable instead of leaving a dead button. */}
            {!canSubmit && !saving && (
              <span className="hidden text-[11px] text-muted-foreground sm:inline">
                {!name.trim() ? 'Name required' : 'Floor plan required'}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              // Wrapped, not passed bare: React hands the click event to the first
              // parameter, which would read as `thenMark = true`.
              onClick={() => submit()}
              disabled={!canSubmit}
              className="rounded-lg bg-brand px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create layout'}
            </button>
          </div>
        </div>
      }
    >
      {/* The Modal shell is padding-free by design — content owns its inset. */}
      <div className="px-5 py-4">
        <div className="grid gap-5 lg:grid-cols-[1.15fr_1fr]">
          {/* ── The plan ──────────────────────────────────────────────── */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Floor plan</h3>

            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                pick(e.target.files?.[0]);
                // Reset so re-picking the SAME file after an error still fires onChange.
                e.target.value = '';
              }}
            />

            {previewSrc ? (
              <div
                {...dropProps}
                className={`group relative overflow-hidden rounded-xl border transition-colors ${
                  dragOver ? 'border-brand ring-2 ring-brand/30' : 'border-border'
                }`}
              >
                {/* A white plate behind the plan: floor plans are usually dark line art on
                    transparency and would disappear against the dark panel. */}
                <div className="flex aspect-[4/3] items-center justify-center bg-white p-2">
                  <img src={previewSrc} alt="" className="max-h-full max-w-full object-contain" />
                </div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="absolute inset-0 flex items-center justify-center bg-black/55 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:outline-none"
                >
                  <span className="flex items-center gap-1.5 rounded-lg bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-900 shadow">
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                    {picked ? 'Choose another' : 'Replace plan'}
                  </span>
                </button>
                {dragOver && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-brand/15">
                    <span className="rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white shadow">
                      Drop to replace
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                {...dropProps}
                className={`flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 transition-colors ${
                  dragOver
                    ? 'border-brand bg-brand/5 text-foreground'
                    : 'border-border text-muted-foreground hover:border-brand/60 hover:bg-muted/40 hover:text-foreground'
                }`}
              >
                <span className={`rounded-full p-2.5 ${dragOver ? 'bg-brand/15 text-brand' : 'bg-muted'}`}>
                  {dragOver ? <ArrowUpTrayIcon className="h-5 w-5" /> : <PhotoIcon className="h-5 w-5" />}
                </span>
                <span className="text-sm font-medium">
                  {dragOver ? 'Drop to upload' : 'Drop a floor plan, or click to browse'}
                </span>
                <span className="text-[11px] text-muted-foreground">SVG, PNG, JPEG or WebP · up to 5 MB</span>
              </button>
            )}

            {/* What's actually loaded, and how to back out of a replacement. */}
            <div className="flex min-h-[22px] flex-wrap items-center gap-1.5">
              {picked ? (
                <>
                  <Chip>
                    {picked.width} × {picked.height} px
                  </Chip>
                  <Chip>{fmtBytes(picked.size)}</Chip>
                  <Chip>{picked.mime.replace('image/', '').replace('svg+xml', 'svg').toUpperCase()}</Chip>
                  {editing && (
                    <button
                      type="button"
                      onClick={() => setPicked(null)}
                      className="text-[11px] font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    >
                      Keep the current plan instead
                    </button>
                  )}
                </>
              ) : (
                editing && (
                  <>
                    <Chip>
                      {layout!.image_width} × {layout!.image_height} px
                    </Chip>
                    <span className="text-[11px] italic text-muted-foreground">Current plan — unchanged</span>
                  </>
                )
              )}
            </div>
          </section>

          {/* ── Details ───────────────────────────────────────────────── */}
          <div className="space-y-4">
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Details</h3>

              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setTouchedName(true)}
                  onKeyDown={(e) => e.key === 'Enter' && submit()}
                  placeholder="Ground floor, Plant A, Warehouse…"
                  autoFocus
                  aria-invalid={!!nameError}
                  className={nameError ? fieldBad : fieldOk}
                />
                {nameError && <p className="mt-1 text-[11px] text-destructive">{nameError}</p>}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Description <span className="font-normal italic">optional</span>
                </label>
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submit()}
                  placeholder="Shown beside the name in the layout list"
                  className={fieldOk}
                />
              </div>
            </section>

            <label
              className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${
                makeDefault ? 'border-brand/50 bg-brand/5' : 'border-border hover:bg-muted/40'
              } ${editing && layout!.is_default ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                checked={makeDefault}
                disabled={editing && layout!.is_default}
                onChange={(e) => setMakeDefault(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-none rounded border-border accent-[color:var(--brand,#015ab5)]"
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <StarIcon className="h-3.5 w-3.5 text-amber-500" /> Open this layout by default
                </span>
                <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                  {editing && layout!.is_default
                    ? 'Already the default. Set another layout as default to change it.'
                    : 'Map View opens here when an operator has no layout of their own selected.'}
                </span>
              </span>
            </label>

            {editing && (
              <p className="text-[11px] leading-snug text-muted-foreground">
                Replacing the plan keeps every pin: positions are stored as fractions of the
                image, so a redrawn floor at a different resolution lines up as before.
              </p>
            )}
          </div>
        </div>

        {error && (
          <p className="mt-4 flex items-start gap-1.5 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <ExclamationCircleIcon className="mt-px h-4 w-4 flex-none" />
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
